import { useState, useEffect, useCallback } from 'react'
import {
  Folder,
  File,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Home,
  Lock,
  RefreshCw,
  FolderOpen,
  X,
  FileText,
} from 'lucide-react'
import Panel from './Panel'
import MarkdownViewer from './MarkdownViewer'
import { resolveApiBase } from '../../lib/mountBase'

interface FileEntry {
  name: string
  path: string
  is_dir: boolean
  is_file: boolean
  size: number | null
  modified: number
  is_blocked: boolean
}

interface FilesListResponse {
  path: string
  base_dir: string
  entries: FileEntry[]
}

interface FileContentResponse {
  path: string
  filename: string
  size: number
  content: string
}

interface FileBrowserPanelProps {
  isMaximized?: boolean
  onMaximize?: () => void
  onClose?: () => void
  closeable?: boolean
  initialPath?: string  // Initial directory path to open (relative to base dir)
  standalone?: boolean  // If true, renders without Panel wrapper
  appName?: string  // If provided, uses app-specific file API for deployed apps
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

function isMarkdownFile(filename: string): boolean {
  return /\.(md|markdown|mdown|mkdn|mkd)$/i.test(filename)
}

export default function FileBrowserPanel({
  isMaximized,
  onMaximize,
  onClose,
  closeable = true,
  initialPath = '',
  standalone = false,
  appName,
}: FileBrowserPanelProps) {
  // Normalize path - for per-app workspaces, paths should be relative to /home/coder/workspace/
  // This handles both main workspace and worktree paths:
  //   /home/coder/workspace/main/... -> main/...
  //   /home/coder/workspace/worktrees/session-name/... -> worktrees/session-name/...
  // For legacy per-user workspaces, paths should be relative to /home/coder/
  const normalizedInitialPath = appName
    ? initialPath.replace(/^\/home\/coder\/workspace\/?/, '')
    : initialPath.replace(/^\/home\/coder\/?/, '')
  const apiBase = resolveApiBase(typeof window !== 'undefined' ? window.location.pathname : '/')
  const [currentPath, setCurrentPath] = useState(normalizedInitialPath)
  const [entries, setEntries] = useState<FileEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set())

  // File viewer state
  const [viewingFile, setViewingFile] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState<string | null>(null)
  const [fileLoading, setFileLoading] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)

  const loadDirectory = useCallback(async (path: string = '') => {
    setLoading(true)
    setError(null)
    try {
      const params = path ? `?path=${encodeURIComponent(path)}` : ''
      // Use app-specific API when appName is provided, otherwise use per-user workspace API
      const apiEndpoint = appName
        ? `${apiBase}/deployed-apps/${encodeURIComponent(appName)}/files${params}`
        : `${apiBase}/workspace/files${params}`
      const response = await fetch(apiEndpoint)
      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Failed to load' }))
        throw new Error(err.detail || 'Failed to load directory')
      }
      const data: FilesListResponse = await response.json()
      setEntries(data.entries)
      setCurrentPath(path)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load directory')
    } finally {
      setLoading(false)
    }
  }, [apiBase, appName])

  useEffect(() => {
    loadDirectory(normalizedInitialPath)
  }, [loadDirectory, normalizedInitialPath])

  async function openFile(path: string) {
    setViewingFile(path)
    setFileLoading(true)
    setFileError(null)
    setFileContent(null)
    try {
      // Use app-specific API when appName is provided
      const apiEndpoint = appName
        ? `${apiBase}/deployed-apps/${encodeURIComponent(appName)}/files/content?path=${encodeURIComponent(path)}`
        : `${apiBase}/workspace/files/content?path=${encodeURIComponent(path)}`
      const response = await fetch(apiEndpoint)
      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Failed to load file' }))
        throw new Error(err.detail || 'Failed to load file')
      }
      const data: FileContentResponse = await response.json()
      setFileContent(data.content)
    } catch (err) {
      setFileError(err instanceof Error ? err.message : 'Failed to load file')
    } finally {
      setFileLoading(false)
    }
  }

  function navigateUp() {
    const parts = currentPath.split('/').filter(Boolean)
    parts.pop()
    loadDirectory(parts.join('/'))
  }

  function navigateToEntry(entry: FileEntry) {
    if (entry.is_dir) {
      loadDirectory(entry.path)
    } else if (!entry.is_blocked) {
      openFile(entry.path)
    }
  }

  function toggleDir(path: string, event: React.MouseEvent) {
    event.stopPropagation()
    setExpandedDirs((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }

  // Generate breadcrumb parts
  const pathParts = currentPath.split('/').filter(Boolean)
  const breadcrumbs = [
    { name: 'Home', path: '' },
    ...pathParts.map((part, i) => ({
      name: part,
      path: pathParts.slice(0, i + 1).join('/'),
    })),
  ]

  const content = (
      <div className={standalone ? "flex flex-col h-[400px] rounded-lg border border-border bg-card overflow-hidden" : "flex flex-col h-full"}>
        {/* Navigation bar */}
        <div className="flex items-center gap-1 p-2 bg-secondary/30 border-b border-border flex-shrink-0">
          <button
            onClick={() => loadDirectory('')}
            className="p-1 rounded hover:bg-background transition-colors"
            title="Go to root"
          >
            <Home className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={navigateUp}
            disabled={!currentPath}
            className="p-1 rounded hover:bg-background transition-colors disabled:opacity-50"
            title="Go up"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => loadDirectory(currentPath)}
            disabled={loading}
            className="p-1 rounded hover:bg-background transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {/* Breadcrumbs */}
          <div className="flex items-center gap-0.5 text-xs overflow-auto flex-1 ml-1">
            {breadcrumbs.map((crumb, i) => (
              <div key={crumb.path} className="flex items-center gap-0.5">
                {i > 0 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                <button
                  onClick={() => loadDirectory(crumb.path)}
                  className="px-1 py-0.5 rounded hover:bg-background transition-colors truncate max-w-[80px]"
                  title={crumb.name}
                >
                  {crumb.name}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="mx-2 mt-2 p-2 rounded bg-destructive/10 text-destructive border border-destructive/20 text-xs">
            {error}
          </div>
        )}

        {/* File list */}
        <div className="flex-1 overflow-auto">
          {loading && entries.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Empty directory
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {entries.map((entry) => (
                <div
                  key={entry.path}
                  onClick={() => navigateToEntry(entry)}
                  className={`flex items-center gap-2 px-3 py-1.5 hover:bg-secondary/50 transition-colors ${
                    entry.is_blocked ? 'opacity-60' : 'cursor-pointer'
                  }`}
                >
                  {/* Expand/collapse for directories */}
                  {entry.is_dir ? (
                    <button
                      onClick={(e) => toggleDir(entry.path, e)}
                      className="p-0.5 rounded hover:bg-background transition-colors"
                    >
                      {expandedDirs.has(entry.path) ? (
                        <ChevronDown className="w-3 h-3 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-3 h-3 text-muted-foreground" />
                      )}
                    </button>
                  ) : (
                    <span className="w-4" />
                  )}

                  {/* Icon */}
                  {entry.is_dir ? (
                    <Folder className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  ) : entry.is_blocked ? (
                    <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  )}

                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs truncate ${entry.is_blocked ? 'text-muted-foreground' : ''}`}>
                      {entry.name}
                    </span>
                  </div>

                  {/* Size */}
                  {entry.is_file && entry.size !== null && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatBytes(entry.size)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* File viewer modal */}
        {viewingFile && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-3 border-b border-border">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="font-medium text-sm truncate">{viewingFile.split('/').pop()}</span>
                </div>
                <button
                  onClick={() => setViewingFile(null)}
                  className="p-1 rounded hover:bg-secondary transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto p-3">
                {fileLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : fileError ? (
                  <div className="p-3 rounded bg-destructive/10 text-destructive text-sm">
                    {fileError}
                  </div>
                ) : fileContent !== null && viewingFile && isMarkdownFile(viewingFile) ? (
                  <MarkdownViewer
                    content={fileContent}
                    appName={appName}
                    currentPath={viewingFile}
                  />
                ) : (
                  <pre className="text-xs font-mono bg-black text-green-400 p-3 rounded overflow-auto whitespace-pre-wrap">
                    {fileContent}
                  </pre>
                )}
              </div>

              {/* Footer */}
              <div className="p-3 border-t border-border flex justify-end">
                <button
                  onClick={() => setViewingFile(null)}
                  className="px-3 py-1.5 rounded border border-border hover:bg-secondary transition-colors text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  )

  if (standalone) {
    return content
  }

  return (
    <Panel
      title="Files"
      icon={<FolderOpen className="w-4 h-4" />}
      isMaximized={isMaximized}
      onMaximize={onMaximize}
      onClose={onClose}
      closeable={closeable}
    >
      {content}
    </Panel>
  )
}
